import type { ExtendedLibrary } from '@seen/core/models/library'
import type { DefaultError } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuSeparator } from '#src/components/core/dropdown-menu/DropdownMenuSeparator.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { extractTitleFromHTML } from '#src/utils/html.ts'

export interface TemplateDeleteDropdownMenuItemProps {
  template: ExtendedLibrary
}

// Renders its own leading separator rather than letting callers place one, so
// that read-only users — who get no delete item at all — are not left with a
// divider hanging off the bottom of the menu.
export function TemplateDeleteDropdownMenuItem({
  template,
}: TemplateDeleteDropdownMenuItemProps): ReactNode {
  const navigate = useNavigate()
  const isReadOnly = useIsReadOnly()
  const mutateAsync = usePatchMutation<ExtendedLibrary>().mutateAsync

  // Matches the templates list, whose actions column renders nothing for
  // read-only users.
  if (isReadOnly) {
    return null
  }

  const title = extractTitleFromHTML(template.title) || 'Untitled'

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuDeleteItem
        deleteConfirmationProps={{
          title: `Delete "${title}"?`,
          description:
            'Deleted templates can still be recovered by contacting our team.',
          onConfirm: () => {
            mutateAsync({
              instance: template,
              mutateFn: (t) => {
                t.status = 'retired'
              },
            })
              .then(() => {
                toast.success(`"${title}" deleted`)
                // Only once the retire has landed: a failed patch leaves the
                // user on a template that still exists, rather than bouncing
                // them back to a list that still lists it.
                void navigate(-1)
              })
              .catch((error: DefaultError) => {
                toast.error(`Failed to delete "${title}": ${error}`)
              })
          },
        }}
      />
    </>
  )
}
